import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {Box, TablePagination, Typography} from "@mui/material";
import {OverviewTableI18N} from "@/translations";

export interface OverviewPaginationProps {
    /** Zero-based current page, mirrors `applied.page`. */
    page: number;
    /** Rows per page, mirrors `applied.size`. */
    rowsPerPage: number;
    totalElements: number;
    /** Used only for the explicit "Seite x von y" text — `TablePagination` itself never shows it. */
    totalPages: number;
    /**
     * The variant's page-size choices (10/25/50), from `REQUEST_SIZE_OPTIONS` / `ORDER_SIZE_OPTIONS`, read
     * through `UseOrderOverviewStateResult.sizeOptions` (`readonly number[]`, Phase 1.2) — `readonly` here
     * so callers never need a defensive copy just to satisfy this prop.
     */
    rowsPerPageOptions: readonly number[];
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (size: number) => void;
}

/**
 * Page navigation shared by every overview table variant (#1607): a standard MUI `TablePagination` plus
 * an explicit "Seite x von y" line the task text requires but `TablePagination` does not show on its own.
 * Holds no state of its own — every prop mirrors the caller's `useOverviewTableState` result, and both
 * callbacks map straight onto `setPage` / `setSize`.
 */
export const OverviewPagination: React.FC<OverviewPaginationProps> = ({
    page,
    rowsPerPage,
    totalElements,
    totalPages,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange
}) => {
    const {t} = useTranslation();

    const handlePageChange = useCallback((_event: unknown, newPage: number) => onPageChange(newPage), [onPageChange]);

    const handleRowsPerPageChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onRowsPerPageChange(Number.parseInt(event.target.value, 10)),
        [onRowsPerPageChange]
    );

    const getItemAriaLabel = useCallback(
        (type: "first" | "last" | "next" | "previous"): string =>
            type === "next" || type === "last"
                ? t(OverviewTableI18N.pagination.nextPage)
                : t(OverviewTableI18N.pagination.previousPage),
        [t]
    );

    // `totalPages` is 0 on an empty result set — showing "page 1 of 0" instead of "page 1 of 1" would be
    // misleading, so the total is floored at 1 for this line only (TablePagination's own `count` still
    // gets the true `totalElements`, unaffected by this floor).
    const displayedTotalPages = Math.max(totalPages, 1);

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: 1}}>
            <Typography variant="body2" color="text.secondary">
                {t(OverviewTableI18N.pagination.pageOfTotal, {page: page + 1, total: displayedTotalPages})}
            </Typography>
            <TablePagination
                component="div"
                count={totalElements}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage={t(OverviewTableI18N.pagination.rowsPerPage)}
                labelDisplayedRows={({from, to, count}) =>
                    t(OverviewTableI18N.pagination.displayedRows, {from, to, count})
                }
                getItemAriaLabel={getItemAriaLabel}
            />
        </Box>
    );
};
